import Axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import type { AlertMessagesPage, RawAlertMessage } from './useContentAlertMessages';

type CachedPages = InfiniteData<AlertMessagesPage>;

// Patches every loaded alert messages list (one per filter set) in place. The filter-options query
// shares the key root but holds a plain array, so anything without pages is left alone.
const patchPages = (
  queryClient: QueryClient,
  update: (page: AlertMessagesPage, index: number) => AlertMessagesPage,
) => {
  queryClient.setQueriesData<CachedPages>({ queryKey: [QueryKeys.ContentAlertMessages] }, (old) => {
    if (!old?.pages) return old;

    return { ...old, pages: old.pages.map(update) };
  });
};

// The shared write path for saves made outside the list (the states modal): re-renders every
// subscribed cell without a refetch.
export const usePatchAlertMessage = () => {
  const queryClient = useQueryClient();

  return (id: number, patch: Partial<RawAlertMessage>) => {
    patchPages(queryClient, page => ({
      ...page,
      data: page.data.map(row => (row.id === id ? { ...row, ...patch } : row)),
    }));
  };
};

// A routed modal's row is normally in the list cache already; the API lookup only runs on a deep
// link, where the list hasn't loaded yet.
export const useAlertMessageTitle = (id: number) => {
  const queryClient = useQueryClient();
  const cached = queryClient
    .getQueriesData<CachedPages>({ queryKey: [QueryKeys.ContentAlertMessages] })
    .flatMap(([, data]) => data?.pages?.flatMap(page => page.data) ?? [])
    .find(row => row.id === id)?.title;

  const { data } = useQuery({
    enabled: cached === undefined,
    // Unlike index (`data` root), show roots the record under the serializer's model name.
    queryFn: () => Axios
      .get<{ alert_message: Pick<RawAlertMessage, 'id' | 'title'> }>(
      Routes.plato_api_alert_message_path(id, { only: ['id', 'title'] }),
    )
      .then(res => res.data.alert_message.title),
    queryKey: [QueryKeys.ContentAlertMessages, 'title', id],
  });

  return cached ?? data;
};

export const usePublishAlertMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, publish }: { id: number; publish: boolean }) => Axios
      .patch<{ data: RawAlertMessage }>(Routes.plato_api_alert_message_path(id), { alert_message: { publish } })
      .then(res => res.data.data),
    onSuccess: (updated) => {
      patchPages(queryClient, page => ({
        ...page,
        data: page.data.map(row => (row.id === updated.id ? { ...row, publish: updated.publish } : row)),
      }));
    },
  });
};

export const useDeleteAlertMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => Axios.delete(Routes.plato_api_alert_message_path(id)).then(() => id),
    onSuccess: (id) => {
      patchPages(queryClient, (page, index) => ({
        data: page.data.filter(row => row.id !== id),
        meta: { count: index === 0 ? page.meta.count - 1 : page.meta.count },
      }));
    },
  });
};
